import { useMemo, useState } from "react";
import { Accordion, Badge, Modal, Form } from "react-bootstrap";
import { Button } from "./Button";
import { InfoCircle, FileEarmarkText, People } from "react-bootstrap-icons";
import type {
  ServerRequestsSubscriptionGroupContractRuleDto,
  ServerRequestsSubscriptionGroupsResponse,
} from "../shared/api/axios-client";

const DEFAULT_STORAGE_KEY = "spotsync-group-parkers-help-dismissed";

const RateType = { Discount: 0, Fee: 1 } as const;

function ContractRulesPreview({
  rules,
}: {
  rules: ServerRequestsSubscriptionGroupContractRuleDto[];
}) {
  const sorted = useMemo(
    () =>
      [...rules].sort((a, b) => (a.min ?? Number.MAX_SAFE_INTEGER) - (b.min ?? Number.MAX_SAFE_INTEGER)),
    [rules]
  );
  return (
    <div className="small">
      <div className="d-flex flex-column gap-2">
        {sorted.map((r, i) => {
          const isNoCost = !r.price || r.price === 0;
          const isFee = r.type === RateType.Fee;
          const displayPrice = Math.abs((r.price ?? 0) / 100);
          return (
            <div
              key={i}
              className="d-flex align-items-center justify-content-between px-2 py-1 rounded border"
            >
              <span>
                {r.min ?? 0} – {r.max ?? "∞"} parkers
                <span className="text-muted ms-1">
                  {isNoCost ? "Free" : isFee ? "fee" : "discount"}
                </span>
              </span>
              <Badge bg={isNoCost ? "secondary" : isFee ? "danger" : "success"} pill>
                {isNoCost ? "No cost" : `${isFee ? "+" : "−"} $${displayPrice.toFixed(2)}`}
              </Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export type GroupParkersHelpDialogProps = {
  show: boolean;
  onClose: () => void;
  /** localStorage key for "don't show again". Defaults to spotsync-group-parkers-help-dismissed */
  storageKey?: string;
  /** Optional custom title (default: "Manage group parkers") */
  title?: string;
  /** Optional custom body content. If not provided, shows default group-parkers copy. */
  children?: React.ReactNode;
  /** When provided, shows groups and their contract details at the top of the modal. */
  groups?: ServerRequestsSubscriptionGroupsResponse[];
};

/** "How it works" section: numbered list in a light pink/peach container with circular number badges. */
const howItWorksContent = (
  <div className="bg-primary-subtle rounded p-3 mt-3">
    <div className="fw-semibold mb-3">How it works</div>
    <div className="d-flex flex-column gap-3">
      <div className="d-flex gap-3 align-items-start">
        <span className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold" style={{ width: 28, height: 28, fontSize: "0.875rem" }}>
          1
        </span>
        <div>
          <div className="fw-semibold">Add parkers to groups</div>
          <div className="text-muted small">Use &quot;Add parker&quot; to add people and assign them to a group.</div>
        </div>
      </div>
      <div className="d-flex gap-3 align-items-start">
        <span className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold" style={{ width: 28, height: 28, fontSize: "0.875rem" }}>
          2
        </span>
        <div>
          <div className="fw-semibold">View contracts</div>
          <div className="text-muted small">Click a group to see its pricing tiers and terms.</div>
        </div>
      </div>
      <div className="d-flex gap-3 align-items-start">
        <span className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold" style={{ width: 28, height: 28, fontSize: "0.875rem" }}>
          3
        </span>
        <div>
          <div className="fw-semibold">Move or edit anytime</div>
          <div className="text-muted small">Reassign parkers between groups as your team changes.</div>
        </div>
      </div>
    </div>
  </div>
);

export function GroupParkersHelpDialog({
  show,
  onClose,
  storageKey = DEFAULT_STORAGE_KEY,
  title = "Managing group parkers",
  children,
  groups = [],
}: GroupParkersHelpDialogProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const hasGroups = groups.length > 0;

  const handleClose = (persistDontShow: boolean) => {
    if (persistDontShow) {
      try {
        localStorage.setItem(storageKey, "true");
      } catch {
        // ignore localStorage errors
      }
    }
    setDontShowAgain(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleClose(dontShowAgain);
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={() => handleClose(false)} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <div>
          <Modal.Title className="d-flex align-items-center gap-2">
            {title}
          </Modal.Title>
          <p className="text-muted small mb-0 mt-1">
            View contracts, pricing, and assign parkers to groups.
          </p>
        </div>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-2 mt-2">
          {hasGroups && (
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <FileEarmarkText className="text-primary fs-5" />
                <span className="fw-semibold text-uppercase small">Your groups &amp; contracts</span>
              </div>
              <Accordion flush className="d-flex flex-column gap-2">
                {groups.map((g) => {
                  const rules = g.contractRules ?? [];
                  const hasContract = rules.length > 0;
                  const name = g.name ?? "Unnamed group";
                  return (
                    <Accordion.Item
                      key={g.id}
                      eventKey={String(g.id)}
                      className="border rounded overflow-hidden bg-white"
                    >
                      <Accordion.Header className="py-2 px-3">
                        <div className="d-flex align-items-center gap-2 w-100">
                          <People className="text-secondary flex-shrink-0" size={20} />
                          <span className="fw-semibold">{name}</span>
                          {hasContract && (
                            <Badge bg="primary" className="text-uppercase fw-normal ms-1">
                              Contract
                            </Badge>
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="pt-0 pb-3 px-3">
                        {hasContract ? (
                          <ContractRulesPreview rules={rules} />
                        ) : (
                          <span className="text-muted small">No contract terms. The base price will be applied.</span>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </div>
          )}
          {children ?? howItWorksContent}

          <Form.Group className="mt-3">
            <Form.Check
              id="dontShowAgain"
              type="checkbox"
              label="Don't show this again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button type="submit" className="btn-main">
            Got it
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/** Returns whether the help dialog has been dismissed (don't show again) for the given key. */
export function isGroupParkersHelpDismissed(storageKey: string = DEFAULT_STORAGE_KEY): boolean {
  try {
    return localStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
}
